/**
 * Sections of the employee profile (EMP-7, Keka's "+ Add New Card"). The three built-in sections —
 * Personal / Work / Bank Information — are fixed in code and always come first; an org adds its own
 * ("Medical", "Statutory", …) after them. Custom fields (employee-custom-fields.contract.ts) pick a
 * section by `sectionKey`.
 *
 * Admin CRUD is `org.settings:read` / `org.settings:update`, like the custom field catalogue. The
 * screens that RENDER fields read the active sections through `listActiveEmployeeFieldSections` on
 * lookup.contract.ts instead, gated by `employee:read`.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, uuidSchema } from '../schemas/common.js';
import { BUILT_IN_EMPLOYEE_FIELD_SECTIONS } from '../lib/built-in-employee-fields.js';
import { employeeFieldKeySchema } from './employee-custom-fields.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * One section, built-in or org-defined. A built-in one has `id: null`, `isBuiltIn: true`, is always
 * active, and can't be renamed, reordered, deactivated or deleted.
 */
export const employeeFieldSectionSchema = z.object({
  id: uuidSchema.nullable(),
  key: z.string(),
  label: z.string(),
  isBuiltIn: z.boolean(),
  isActive: z.boolean(),
  /** Display order across all sections (built-ins first). */
  sortOrder: z.number().int(),
});
export type EmployeeFieldSection = z.infer<typeof employeeFieldSectionSchema>;

const labelSchema = z.string().trim().min(1).max(100);

export const createEmployeeFieldSectionBodySchema = z.object({
  key: employeeFieldKeySchema.refine(
    (key) => !(BUILT_IN_EMPLOYEE_FIELD_SECTIONS as readonly string[]).includes(key),
    'That key belongs to a built-in section',
  ),
  label: labelSchema,
});

/** The key is immutable — custom fields reference it. */
export const updateEmployeeFieldSectionBodySchema = z.object({
  label: labelSchema.optional(),
  isActive: z.boolean().optional(),
});

/** Every org-defined section's id, in the new order — a partial list is a 422. */
export const reorderEmployeeFieldSectionsBodySchema = z.object({
  sectionIds: z.array(uuidSchema).min(1).max(100),
});

const listResponseSchema = z.object({ items: z.array(employeeFieldSectionSchema) });

const readMeta = routeMeta('org.settings:read');
const writeMeta = routeMeta('org.settings:update');

export const employeeFieldSectionsContract = c.router(
  {
    listEmployeeFieldSections: {
      method: 'GET',
      path: '/employee-field-sections',
      responses: { 200: listResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'List the employee profile sections — built-in first, then the org’s own, active or not',
      metadata: readMeta,
    },
    createEmployeeFieldSection: {
      method: 'POST',
      path: '/employee-field-sections',
      body: createEmployeeFieldSectionBodySchema,
      responses: {
        201: employeeFieldSectionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Add a section to the employee profile; it goes after the existing ones',
      metadata: writeMeta,
    },
    reorderEmployeeFieldSections: {
      method: 'PUT',
      path: '/employee-field-sections/order',
      body: reorderEmployeeFieldSectionsBodySchema,
      responses: {
        200: listResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Set the order of the org’s own profile sections (built-ins always come first)',
      metadata: writeMeta,
    },
    updateEmployeeFieldSection: {
      method: 'PATCH',
      path: '/employee-field-sections/:sectionId',
      body: updateEmployeeFieldSectionBodySchema,
      responses: {
        200: employeeFieldSectionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Rename a profile section, or hide it (and its fields) by deactivating it',
      metadata: writeMeta,
    },
    deleteEmployeeFieldSection: {
      method: 'DELETE',
      path: '/employee-field-sections/:sectionId',
      responses: {
        200: z.object({ success: z.boolean() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete an empty profile section (refused while any custom field uses it)',
      metadata: writeMeta,
    },
  },
  { pathPrefix: '/organisation' },
);
